import React, { useState, useEffect, useCallback } from 'react';
import { DemographicsWorkflow } from './demographics-workflow';
import { AnnotationSession, type SessionSong, type AnnotationSessionState } from './annotation-session';
import { SessionCompletion, type SessionCompletionStats, type FeedbackData } from './session-completion';
import { type AnnotationFormData } from './annotation-form';
import { annotationSessionService, type SessionConfig } from '@/client/services/annotationSessions';
import { FeedbackContextService } from '@/services/feedbackContext';
import { useAuth } from '@/components/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Trophy, Clock, Music, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

interface AnnotationWorkflowProps {
  onComplete?: (sessionId: string, participantId: string) => void;
  onError?: (error: string) => void;
  sessionConfig?: SessionConfig;
}

type WorkflowState =
  | 'demographics'
  | 'session-loading'
  | 'annotation'
  | 'completed'
  | 'error';

interface CompletionStats {
  totalSongs: number;
  totalTime: number;
  averageTimePerSong: number;
  sessionId: string;
  participantId: string;
  completedAt: string;
}

export function AnnotationWorkflow({ onComplete, onError, sessionConfig }: AnnotationWorkflowProps) {
  const { participantProfile, setParticipantProfile } = useAuth();
  const [workflowState, setWorkflowState] = useState<WorkflowState>(
    participantProfile?.hasProfile ? 'session-loading' : 'demographics'
  );
  const [participantId, setParticipantId] = useState<string>(participantProfile?.id || '');
  const [sessionState, setSessionState] = useState<AnnotationSessionState | null>(null);
  const [sessionSongs, setSessionSongs] = useState<SessionSong[]>([]);
  const [completionStats, setCompletionStats] = useState<CompletionStats | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [sessionStartTime, setSessionStartTime] = useState<number>(0);
  const [isCreatingSession, setIsCreatingSession] = useState<boolean>(false);

  // Update workflow state based on participant profile
  useEffect(() => {
    if (participantProfile?.hasProfile && workflowState === 'demographics') {
      setParticipantId(participantProfile.id);
      setWorkflowState('session-loading');
    }
  }, [participantProfile, workflowState]);

  const handleDemographicsComplete = useCallback(async (newParticipantId: string) => {
    // Prevent multiple simultaneous session creation attempts
    if (isCreatingSession) {
      return;
    }

    try {
      setIsCreatingSession(true);
      setParticipantId(newParticipantId);

      // Update auth context with participant profile
      setParticipantProfile({
        id: newParticipantId,
        hasProfile: true
      });

      // Store participant context for feedback
      FeedbackContextService.setCurrentParticipantId(newParticipantId);

      setWorkflowState('session-loading');

      // Create annotation session using API
      const response = await annotationSessionService.createSession({
        participantId: newParticipantId,
        sessionConfig
      });

      // Get session songs
      const songsResponse = await annotationSessionService.getSessionSongs(response.sessionId);

      setSessionState(response.sessionState);
      setSessionSongs(songsResponse.sessionSongs);
      setSessionStartTime(Date.now());

      // Store session context for feedback
      FeedbackContextService.setCurrentSessionId(response.sessionId);

      setWorkflowState('annotation');

      toast.success(response.message);

    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to create annotation session';
      setErrorMessage(errorMsg);
      setWorkflowState('error');
      onError?.(errorMsg);
    } finally {
      setIsCreatingSession(false);
    }
  }, [isCreatingSession, setParticipantProfile, sessionConfig, onError]);

  // Auto-start session if participant profile exists
  useEffect(() => {
    if (participantProfile?.hasProfile && participantId && workflowState === 'session-loading' && !isCreatingSession) {
      handleDemographicsComplete(participantId);
    }
  }, [participantProfile?.hasProfile, participantId, workflowState, isCreatingSession, handleDemographicsComplete]);

  const handleAnnotationSubmit = async (annotation: AnnotationFormData, duration: number, listeningTime: number, songDuration?: number, listeningPercentage?: number) => {
    if (!sessionState || !sessionState.currentSong) return;

    try {
      // Convert form data to API request format
      const annotationRequest = annotationSessionService.convertAnnotationData(
        annotation,
        sessionState.currentSong.id,
        sessionState.currentSong.source,
        duration,
        listeningTime,
        songDuration,
        listeningPercentage
      );

      // Submit annotation via API
      const response = await annotationSessionService.submitAnnotation(
        sessionState.sessionId,
        annotationRequest
      );

      if (response.isComplete) {
        handleSessionComplete();
      } else if (response.sessionState) {
        // Update session state with the latest progress
        setSessionState(response.sessionState);
      }

      toast.success(response.message);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to submit annotation';
      throw new Error(errorMsg);
    }
  };

  const handleSessionComplete = () => {
    if (!sessionState) return;

    const totalTime = Date.now() - sessionStartTime;
    const stats: SessionCompletionStats = {
      sessionId: sessionState.sessionId,
      participantId: sessionState.participantId,
      totalSongs: sessionState.totalSongs,
      totalTime,
      averageTimePerSong: totalTime / sessionState.totalSongs,
      completedAt: new Date().toISOString()
    };

    setCompletionStats(stats);
    setWorkflowState('completed');

    toast.success('Annotation session completed!');
  };

  const handleCompletionFinished = (feedback?: FeedbackData) => {
    if (feedback) {
      console.log('Participant feedback:', feedback);
      // TODO: Submit feedback to API
    }

    // Clear feedback context when session is complete
    FeedbackContextService.clearContext();

    if (sessionState) {
      onComplete?.(sessionState.sessionId, sessionState.participantId);
    }
  };

  const handleError = (error: string) => {
    setErrorMessage(error);
    setWorkflowState('error');
    onError?.(error);
  };

  const handleRetry = () => {
    setErrorMessage('');
    setWorkflowState('demographics');
  };

  const formatTime = (milliseconds: number): string => {
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Demographics phase
  if (workflowState === 'demographics') {
    return (
      <DemographicsWorkflow
        onComplete={handleDemographicsComplete}
        onError={handleError}
      />
    );
  }

  // Session loading phase
  if (workflowState === 'session-loading') {
    return (
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Music className="h-5 w-5" />
              Preparing Your Session
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-center space-x-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              <span>Creating your personalized annotation session...</span>
            </div>
            <div className="text-sm text-muted-foreground text-center">
              <p>We're selecting songs and setting up your session.</p>
              <p>This will only take a moment.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Annotation phase
  if (workflowState === 'annotation' && sessionState && sessionSongs.length > 0) {
    return (
      <AnnotationSession
        sessionState={sessionState}
        sessionSongs={sessionSongs}
        onAnnotationSubmit={handleAnnotationSubmit}
        onSessionComplete={handleSessionComplete}
        onError={handleError}
        allowNavigation={false}
      />
    );
  }

  // Completion phase
  if (workflowState === 'completed' && completionStats) {
    return (
      <SessionCompletion
        stats={completionStats}
        onComplete={handleCompletionFinished}
        showFeedbackForm={true}
        className="w-full"
      />
    );
  }

  // Error phase
  if (workflowState === 'error') {
    return (
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="text-destructive">Session Error</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              We encountered an error during your annotation session:
            </p>
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
              <p className="text-destructive text-sm">{errorMessage}</p>
            </div>
            <div className="flex justify-center">
              <Button onClick={handleRetry}>
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Fallback loading state
  return (
    <div className="max-w-2xl mx-auto">
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center space-x-2">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            <span>Loading...</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}